import { useId, useState } from 'react'

import {
  finishDrill,
  noLearningNeeded,
  startAutomaticLearning,
  startDrill,
  type AcquisitionRound,
  type CardFilter,
  type LearningParameters,
  type ToughCard,
} from '../../api/flashcards'
import { asFailure, type Failure } from '../../api/request'
import { navigate } from '../../frame/route'
import { useAnnounce } from '../../ui/Announce'
import { Button } from '../../ui/Control'
import { Preview } from '../../ui/preview/Preview'
import { Failed } from '../../ui/Status'
import { TOUGH_REASON_COPY } from './CardStatus'
import { Drill, type DrillResult } from './Drill'

interface Achieved {
  readonly nut: ToughCard
  readonly line: string
  readonly done: number
  readonly target: number
}

/**
 * One acquisition session, filling the screen the way a learning round does.
 *
 * Learning by heart is work that wants the same undisturbed surface as answering questions: one card,
 * one step, one thing to do. The two steps look deliberately unlike each other — writing your line is
 * a page with both sides of the card on it, typing it is a bare field with a counter — because doing
 * the wrong one by accident is the one confusion this screen must not allow.
 */
export function RoteSession({
  nuts,
  filter,
  filterId,
  parameters,
  onLeave,
  onChanged,
}: {
  readonly nuts: readonly ToughCard[]
  readonly filter: CardFilter
  readonly filterId?: string
  readonly parameters: LearningParameters
  readonly onLeave: () => void
  readonly onChanged: () => void
}) {
  const announce = useAnnounce()
  const [at, setAt] = useState(0)
  const [round, setRound] = useState<AcquisitionRound | null>(null)
  const [achieved, setAchieved] = useState<readonly Achieved[]>([])
  const [line, setLine] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<Failure | null>(null)
  const fieldId = useId()
  const hintId = useId()
  const nut = nuts[at]

  const attempt = async (work: () => Promise<void>) => {
    if (busy) return
    try {
      setBusy(true)
      setFailure(null)
      await work()
    } catch (error) {
      setFailure(asFailure(error))
    } finally {
      setBusy(false)
    }
  }

  const advance = (kept: readonly Achieved[], stop: boolean) => {
    setAchieved(kept)
    setRound(null)
    setLine('')
    setAt(stop ? nuts.length : at + 1)
    onChanged()
  }

  if (nut === undefined) {
    return (
      <div className="rote-run rote-run--summary">
        <header className="rote-run__head">
          <span className="rote-run__step">Acquisition</span>
          <h2>What you produced</h2>
        </header>
        <main className="rote-run__body">
          {achieved.length === 0 ? (
            <p className="rote-run__note">Nothing was produced this time.</p>
          ) : (
            <ul className="rote-summary__list">
              {achieved.map((one) => (
                <li key={`${one.nut.cardId}-${one.nut.direction}`}>
                  <strong>{one.line}</strong>
                  <span>{`${one.done} of ${one.target} repetitions`}</span>
                </li>
              ))}
            </ul>
          )}
          <p className="rote-run__note">
            A retrieval is worth more in a few hours than right now — immediately after acquiring
            something, remembering it is almost given away.
          </p>
        </main>
        {failure !== null && <Failed failure={failure} />}
        <footer className="rote-run__actions">
          <Button onClick={onLeave}>Back to the cards</Button>
          <Button
            tone="primary"
            disabled={busy}
            onClick={() => {
              void attempt(async () => {
                const run = await startAutomaticLearning(
                  filter,
                  {
                    roundSize: parameters.roundSize,
                    minimumReviewPercentage: parameters.minimumReviewPercentage,
                  },
                  false,
                  filterId,
                )
                navigate('cards', ['ask', run.id])
              })
            }}
          >
            Ask me anyway
          </Button>
        </footer>
      </div>
    )
  }

  if (round !== null) {
    const finish = (result: DrillResult, completed: boolean) => {
      void attempt(async () => {
        await finishDrill(round.id, result.repetitionsDone, result.mistakes, result.lastTyped)
        advance(
          [
            ...achieved,
            {
              nut,
              line: round.line,
              done: result.repetitionsDone,
              target: round.repetitionsTarget,
            },
          ],
          !completed,
        )
      })
    }
    return (
      <div className="rote-run rote-run--drill">
        <SessionHead at={at} total={nuts.length} step={2} busy={busy} onStop={onLeave} />
        {failure !== null && <Failed failure={failure} />}
        <Drill
          card={nut}
          line={round.line}
          repetitions={round.repetitionsTarget}
          busy={busy}
          onFinish={finish}
        />
      </div>
    )
  }

  return (
    <div className="rote-run rote-run--line">
      <SessionHead at={at} total={nuts.length} step={1} busy={busy} onStop={onLeave} />

      <main className="rote-run__body">
        <div className="rote-step__sides">
          <section>
            <span>Question</span>
            <Preview text={nut.question} format={nut.questionFormat} label="Question" />
          </section>
          <section>
            <span>Answer</span>
            <Preview text={nut.answer} format={nut.answerFormat} label="Answer" />
          </section>
        </div>

        <ul className="rote-step__reasons">
          {nut.reasons.map((reason) => (
            <li key={reason}>{TOUGH_REASON_COPY[reason]}</li>
          ))}
        </ul>

        <div className="card-field rote-step__field">
          <label className="card-field__label" htmlFor={fieldId}>
            Your mnemonic
          </label>
          <input
            id={fieldId}
            className="card-field__control"
            value={line}
            maxLength={300}
            spellCheck={false}
            aria-describedby={hintId}
            autoFocus
            placeholder="One line in your own words, e.g. Willensauslegung: 133 II BGB"
            onChange={(event) => {
              setLine(event.target.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && line.trim() !== '') {
                event.preventDefault()
                start()
              }
            }}
          />
          <p className="card-field__hint" id={hintId}>
            Your own words, and typable with one thumb — write “133 II BGB” rather than hunting for
            a §. MyLib proposes nothing and remembers nothing from last time: writing the line again
            is the exercise. It is kept with what you produced, never at the card.
          </p>
        </div>
      </main>

      {failure !== null && <Failed failure={failure} />}

      <footer className="rote-run__actions">
        <Button
          tone="bare"
          disabled={busy}
          onClick={() => {
            void attempt(async () => {
              await noLearningNeeded(nut.cardId)
              announce('Marked as known. It will be asked like any other card.')
              advance(achieved, false)
            })
          }}
        >
          No learning needed
        </Button>
        <Button
          disabled={busy}
          onClick={() => {
            advance(achieved, false)
          }}
        >
          Skip for now
        </Button>
        <Button tone="primary" disabled={busy || line.trim() === ''} onClick={start}>
          Type it <kbd>Enter</kbd>
        </Button>
      </footer>
    </div>
  )

  function start() {
    if (nut === undefined || line.trim() === '') return
    void attempt(async () => {
      setRound(await startDrill(nut.cardId, line.trim(), parameters.drillRepetitions))
    })
  }
}

/** The same head above both steps, so that where you are never depends on reading the body. */
function SessionHead({
  at,
  total,
  step,
  busy,
  onStop,
}: {
  readonly at: number
  readonly total: number
  readonly step: 1 | 2
  readonly busy: boolean
  readonly onStop: () => void
}) {
  return (
    <header className="rote-run__head">
      <span className="rote-run__step">
        {step === 1 ? 'Step 1 · Write your line' : 'Step 2 · Type it from memory'}
      </span>
      <span className="rote-run__position">{`Card ${at + 1} of ${total}`}</span>
      <progress value={at} max={total} />
      <Button tone="bare" className="card-action--danger" disabled={busy} onClick={onStop}>
        Stop
      </Button>
    </header>
  )
}
